import { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { HeartPulse } from 'lucide-react';
import { nexaApi } from './api';

const MiniBar = ({ label, value, color }) => (
  <div className="flex-1" data-testid={`nexa-emotion-bar-${label.toLowerCase()}`}>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[10px] font-mono tracking-widest uppercase text-white/40">{label}</span>
      <span className="text-[11px] font-mono" style={{ color }}>{value ?? '–'}/10</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/[0.07] overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700"
        style={{ width: `${(value || 0) * 10}%`, background: color }} />
    </div>
  </div>
);

export const EmotionSection = () => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    nexaApi.get('/emotions?days=14').then(({ data }) => setRows(data.emotions)).catch(() => setRows([]));
  }, []);

  if (rows === null) return null;

  const latest = rows[rows.length - 1];
  const byDay = {};
  rows.forEach((r) => {
    if (!r.valence) return;
    (byDay[r.date] = byDay[r.date] || []).push(r.valence);
  });
  const chart = Object.entries(byDay).map(([date, vals]) => ({
    day: date.slice(5),
    valence: Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10,
  }));

  return (
    <div data-testid="nexa-emotion-section">
      <div className="flex items-center gap-2 mb-3">
        <HeartPulse className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
        <span className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono">
          Emotional state
        </span>
      </div>
      <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-5">
        {!latest ? (
          <p className="text-[13px] text-white/40 leading-relaxed">
            Nothing detected yet — NEXA quietly reads how you feel from your messages and it will show up here.
          </p>
        ) : (
          <>
            <p className="text-lg text-white/90 capitalize" data-testid="nexa-emotion-mood">{latest.mood}</p>
            <p className="text-[12px] text-white/40 mt-1 leading-relaxed">{latest.note}</p>
            <div className="flex gap-4 mt-4">
              <MiniBar label="Mood" value={latest.valence} color="#38BDF8" />
              <MiniBar label="Energy" value={latest.energy} color="#34D399" />
              <MiniBar label="Stress" value={latest.stress} color="#F87171" />
            </div>
            {chart.length > 1 && (
              <div className="h-28 mt-5 -ml-2" data-testid="nexa-emotion-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chart} margin={{ top: 5, right: 5, bottom: 0, left: -28 }}>
                    <XAxis dataKey="day" tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)' }}
                      axisLine={false} tickLine={false} />
                    <YAxis domain={[1, 10]} tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)' }}
                      axisLine={false} tickLine={false} />
                    <ReferenceLine y={5.5} stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />
                    <Tooltip contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 12, fontSize: 12 }} labelStyle={{ color: 'rgba(255,255,255,0.5)' }} />
                    <Line type="monotone" dataKey="valence" stroke="#38BDF8" strokeWidth={2}
                      dot={{ r: 3, fill: '#38BDF8' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <p className="text-[10px] font-mono text-white/25 mt-3 tracking-wide">
              14-DAY MOOD TREND · DETECTED FROM YOUR MESSAGES
            </p>
          </>
        )}
      </div>
    </div>
  );
};
